// =============================================================================
// legal-entity-client.http.ts — Adaptateur HTTP RÉEL du LegalEntityClient (T7-MULTI-ETAB).
// =============================================================================
// Remplace le stub-first derrière le MÊME DI seam (LegalEntityClientProvider) : les hooks/
// écrans consomment l'interface LegalEntityClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, lecture cross-tenant sous withAdmin, RLS).
// Cet adaptateur ne porte AUCUNE règle métier : il parse la réponse et mappe les erreurs.
//
// SÉCURITÉ (RULE_REF_006) : la session admin est portée par le COOKIE httpOnly posé par
// l'API (`credentials: 'include'`). LECTURE (GET) : aucun en-tête CSRF (parité HttpLegalAdminClient).
// Le `siren` est encodé dans l'URL (ressource server-set, validée `^\d{9}$` côté back — invariant n°1).

import {
  LegalEntityApiError,
  type LegalEntityHierarchyDto,
  type LegalEntityClient,
} from '@/api/legal-entity-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/**
 * Adaptateur HTTP réel du client hiérarchie. `baseUrl` par défaut `/api` (SPA et API
 * partagent l'origine, ou proxy). En test, fetch est mocké.
 */
export class HttpLegalEntityClient implements LegalEntityClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getHierarchy(siren: string): Promise<LegalEntityHierarchyDto> {
    return this.get<LegalEntityHierarchyDto>(
      `/admin/legal-entities/${encodeURIComponent(siren)}/establishments`,
    );
  }

  /** GET same-origin (cookie httpOnly), parse le JSON typé, ou lève une LegalEntityApiError. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse d'erreur en LegalEntityApiError (message serveur, sinon repli). */
  private async toApiError(response: Response): Promise<LegalEntityApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la hiérarchie multi-établissement.`;
    return new LegalEntityApiError(message, response.status);
  }
}
